// src/pages/SupplierTeachers.jsx
//
// Per clarified spec: joining the team is NOT tied to a specific GEFEN
// program. A teacher is either on the supplier's team or not — once on
// the team, they become selectable as `teacher_id` when the supplier
// creates *any* course, public or program-based. The team itself is
// just a roster, independent of `program_teachers`.
//
// DATA SOURCE — both halves are now real:
//   GET    /api/teachers            — platform-wide approved directory
//   GET    /api/supplier/team       — this supplier's actual roster
//   POST   /api/supplier/team       — add to team
//   DELETE /api/supplier/team/:id   — remove from team (soft, status='REMOVED')
// All backed by the supplier_teachers table + TeachersController's
// teamIndex()/teamAdd()/teamRemove(). No more local-only state.
import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import SupplierTeachersHeader from '../components/ui/SupplierTeachersHeader';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

// Same API_BASE resolution used elsewhere (SearchContext.jsx,
// SupplierProgramsContext.jsx, MyCourses.jsx).
const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const API_BASE = ENV_BASE.replace(/\/+$/, '') || 'https://www.doroosonline.co.il';

function authHeaders() {
  const token = localStorage.getItem('jwt') ?? localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiRequest(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    mode: 'cors',
    credentials: 'omit',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || data?.message || 'שגיאת שרת');
  return data;
}

const fetchApprovedTeachers = () =>
  apiRequest('/api/teachers').then((data) => (Array.isArray(data?.items) ? data.items : []));

const fetchMyTeam = () =>
  apiRequest('/api/supplier/team').then((data) => ({
    items: Array.isArray(data?.items) ? data.items : [],
    pending: Array.isArray(data?.pending) ? data.pending : [],
  }));

const addToTeam = (teacherId) => apiRequest('/api/supplier/team', { method: 'POST', body: { teacherId } });

const removeFromTeam = (teacherId) => apiRequest(`/api/supplier/team/${teacherId}`, { method: 'DELETE' });

const SupplierTeachers = () => {
  const { showAlert } = useUser();

  const [directory, setDirectory] = useState([]);
  const [team, setTeam] = useState([]);
  // Invited teachers who haven't approved via the emailed link yet.
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadAll = () => {
    setLoading(true);
    return Promise.all([fetchApprovedTeachers(), fetchMyTeam()])
      .then(([dirItems, teamData]) => {
        setDirectory(dirItems);
        setTeam(teamData.items);
        setPending(teamData.pending);
      })
      .catch((err) => {
        showAlert?.(err.message || 'שגיאה בטעינת מורים', 'warning');
        setDirectory([]);
        setTeam([]);
        setPending([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamIds = useMemo(() => new Set(team.map((t) => t.id)), [team]);
  const pendingIds = useMemo(() => new Set(pending.map((t) => t.id)), [pending]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    const withTeamFlag = directory.map((t) => ({ ...t, inTeam: teamIds.has(t.id), invited: pendingIds.has(t.id) }));
    if (!q) return withTeamFlag;
    return withTeamFlag.filter((t) => {
      const fullName = `${t.firstName} ${t.lastName}`.toLowerCase();
      const subjectMatch = (t.subjects || []).some((s) => s.toLowerCase().includes(q));
      return fullName.includes(q) || subjectMatch;
    });
  }, [directory, teamIds, pendingIds, query]);

  const handleAdd = async (teacherId) => {
    setBusyId(teacherId);
    try {
      const result = await addToTeam(teacherId);
      if (result?.status === 'ACTIVE') {
        showAlert?.('המורה כבר בצוות', 'success');
      } else if (result?.emailSent === false) {
        showAlert?.('ההזמנה נשמרה אך שליחת המייל למורה נכשלה. נסו שוב מאוחר יותר.', 'warning');
      } else {
        showAlert?.('נשלח מייל למורה לאישור ההצטרפות. המורה יצורף לצוות לאחר האישור.', 'success');
      }
      await loadAll();
    } catch (err) {
      showAlert?.(err.message || 'הוספת המורה נכשלה', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const openRemoveConfirm = (teacher) => setRemoveTarget(teacher);
  const closeRemoveConfirm = () => setRemoveTarget(null);

  const handleConfirmRemove = async () => {
    const teacher = removeTarget;
    if (!teacher) return closeRemoveConfirm();
    setBusyId(teacher.id);
    try {
      await removeFromTeam(teacher.id);
      showAlert?.(teacher.invited ? 'ההזמנה בוטלה' : 'המורה הוסר/ה מהצוות', 'success');
      await loadAll();
    } catch (err) {
      showAlert?.(err.message || 'הסרת המורה נכשלה', 'error');
    } finally {
      setBusyId(null);
      closeRemoveConfirm();
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100" dir="rtl">
        <SupplierTeachersHeader />

        <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 pt-2 pb-10 space-y-8">
          {/* Directory search */}
          <section>
            <h2 className="text-sm font-bold text-gray-700 mb-3">חיפוש במאגר המורים המאושרים</h2>

            <div className="relative mb-4 max-w-md">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="חיפוש לפי שם או נושא (למשל: כימיה)"
                dir="rtl"
                className="block w-full h-11 px-4 text-sm rounded-xl border border-gray-200 bg-white text-gray-800 text-right
                           outline-none transition-all duration-150 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100
                           hover:border-indigo-300"
              />
              <svg
                className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 left-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {loading ? (
              <div className="relative min-h-[160px]">
                <SpinnerOverlay variant="glow" text="טוען מורים מאושרים..." dark={false} blur={false} />
              </div>
            ) : searchResults.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 py-10 text-center">
                <p className="text-sm text-gray-500">לא נמצאו מורים מאושרים התואמים לחיפוש.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {searchResults.map((teacher) => (
                  <TeacherCard
                    key={teacher.id}
                    teacher={teacher}
                    busy={busyId === teacher.id}
                    onAdd={() => handleAdd(teacher.id)}
                    onRemove={() => openRemoveConfirm(teacher)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* My team */}
          <section>
            <h2 className="text-sm font-bold text-gray-700 mb-3">הצוות שלי ({team.length})</h2>

            {loading ? null : team.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 py-10 text-center">
                <p className="text-sm text-gray-500">
                  עדיין לא צירפתם מורים לצוות. חפשו במאגר למעלה כדי להתחיל.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {team.map((teacher) => (
                  <TeacherCard
                    key={teacher.id}
                    teacher={{ ...teacher, inTeam: true }}
                    busy={busyId === teacher.id}
                    onRemove={() => openRemoveConfirm(teacher)}
                  />
                ))}
              </div>
            )}

            {!loading && pending.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-bold text-gray-700 mb-3">ממתינים לאישור המורה ({pending.length})</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pending.map((teacher) => (
                    <TeacherCard
                      key={teacher.id}
                      teacher={{ ...teacher, invited: true }}
                      busy={busyId === teacher.id}
                      onRemove={() => openRemoveConfirm({ ...teacher, invited: true })}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={closeRemoveConfirm}
        onConfirm={handleConfirmRemove}
        title={removeTarget?.invited ? 'לבטל את ההזמנה?' : 'להסיר מהצוות?'}
        body={
          removeTarget
            ? removeTarget.invited
              ? `האם לבטל את ההזמנה שנשלחה ל-${removeTarget.firstName} ${removeTarget.lastName}? הקישור שנשלח במייל יפסיק לעבוד.`
              : `האם להסיר את ${removeTarget.firstName} ${removeTarget.lastName} מצוות המורים? המורה לא יהיה זמין לבחירה בקורסים חדשים, אך שיוכים קיימים לקורסים פעילים לא יושפעו.`
            : ''
        }
        confirmText={removeTarget?.invited ? 'בטל הזמנה' : 'הסר מהצוות'}
        cancelText="ביטול"
        danger
      />
    </>
  );
};

function TeacherCard({ teacher, busy, onAdd, onRemove }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Team-membership accent bar */}
      <div className={`h-1 w-full ${teacher.inTeam ? 'bg-emerald-400' : teacher.invited ? 'bg-amber-400' : 'bg-gray-200'}`} />

      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          {teacher.avatar ? (
            <img
              src={teacher.avatar}
              alt={`${teacher.firstName} ${teacher.lastName}`}
              className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-gray-100"
              onError={(e) => {
                // Broken/expired photo URL — fall back to the letter
                // circle instead of a broken-image icon.
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-11 h-11 rounded-full bg-indigo-100 text-indigo-700 font-bold items-center justify-center flex-shrink-0"
            style={{ display: teacher.avatar ? 'none' : 'flex' }}
          >
            {(teacher.firstName || '?').charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900 text-sm truncate">
              {teacher.firstName} {teacher.lastName}
            </h3>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.958a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.363 1.118l1.287 3.957c.3.922-.755 1.688-1.539 1.118l-3.367-2.446a1 1 0 00-1.176 0l-3.367 2.446c-.783.57-1.838-.196-1.539-1.118l1.287-3.957a1 1 0 00-.363-1.118L2.063 9.385c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.958z" />
              </svg>
              <span>{Number(teacher.rating || 0).toFixed(1)}</span>
              <span className="text-gray-300">·</span>
              <span>{teacher.ratingCount || 0} דירוגים</span>
            </div>
          </div>
          {teacher.inTeam && (
            <span className="flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 whitespace-nowrap">
              בצוות
            </span>
          )}
          {!teacher.inTeam && teacher.invited && (
            <span className="flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap">
              ממתין לאישור
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {(teacher.subjects || []).length === 0 ? (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-50 text-gray-400">
              אין עדיין קורסים בפלטפורמה
            </span>
          ) : (
            teacher.subjects.map((s) => (
              <span key={s} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {s}
              </span>
            ))
          )}
        </div>

        <p className="text-xs text-gray-400 mb-4">
          {teacher.coursesTaught > 0
            ? `מלמד/ת ${teacher.coursesTaught} קורסים אצלנו כרגע`
            : 'לא מלמד/ת אצלנו כרגע'}
        </p>

        {teacher.invited && !teacher.inTeam ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="w-full flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 transition disabled:opacity-50"
          >
            {busy ? 'מבטל…' : 'בטל הזמנה'}
          </button>
        ) : teacher.inTeam ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="w-full flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
            </svg>
            {busy ? 'מסיר…' : 'הסר מהצוות'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onAdd}
            disabled={busy}
            className="w-full flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16M20 12H4" />
            </svg>
            {busy ? 'מוסיף…' : 'הוסף לצוות'}
          </button>
        )}
      </div>
    </div>
  );
}

export default SupplierTeachers;